import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, message } from 'antd';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  TEST_ADDRESS_WHITELIST_RULE,
  TestAddressWhitelistRuleResponse,
  TestAddressWhitelistRuleInput,
} from '../graphql/addressWhitelistRuleGraphql';

// 规则条件（由抽屉当前表单计算后传入），用于测试匹配
export type RuleConditions = Omit<
  TestAddressWhitelistRuleInput,
  'testStreetAddress' | 'testUnit' | 'testCity' | 'testState' | 'testZipCode'
>;

interface TestRuleModalProps {
  visible: boolean;
  conditions: RuleConditions | null;
  onClose: () => void;
  onPass: () => void;
}

interface TestState {
  matched: boolean;
  fullAddress: string;
}

const TestRuleModal: React.FC<TestRuleModalProps> = ({ visible, conditions, onClose, onPass }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [result, setResult] = useState<TestState | null>(null);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setResult(null);
    }
  }, [visible, form]);

  const [testRule, { loading }] = useLazyQuery<TestAddressWhitelistRuleResponse>(
    TEST_ADDRESS_WHITELIST_RULE,
    { fetchPolicy: 'network-only' }
  );

  const handleTest = async () => {
    if (!conditions) {
      return;
    }
    let values: {
      testStreetAddress: string;
      testUnit?: string;
      testCity: string;
      testState: string;
      testZipCode: string;
    };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const input: TestAddressWhitelistRuleInput = {
      ...conditions,
      testStreetAddress: values.testStreetAddress.trim(),
      testUnit: values.testUnit?.trim() || null,
      testCity: values.testCity.trim(),
      testState: values.testState.trim(),
      testZipCode: values.testZipCode.trim(),
    };
    const { data, error } = await testRule({ variables: { input } });
    if (error) {
      message.error(error.message);
      setResult(null);
      return;
    }
    const res = data?.testAddressWhitelistRule;
    if (!res) {
      setResult(null);
      return;
    }
    setResult({ matched: res.matched, fullAddress: res.fullAddress });
    if (res.matched) {
      message.success(t('mapTool.addressWhitelistRule.testModal.passMessage') as string);
      onPass();
    }
  };

  return (
    <Modal
      title={t('mapTool.addressWhitelistRule.testModal.title') as string}
      open={visible}
      onCancel={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('components.button.close')}</Button>
          <Button type="primary" loading={loading} onClick={handleTest}>
            {t('mapTool.addressWhitelistRule.testModal.buttonTest')}
          </Button>
        </div>
      }
    >
      <p className="text-gray-500 mb-3 text-sm">
        {t('mapTool.addressWhitelistRule.testModal.hint')}
      </p>
      <Form form={form} layout="vertical">
        <Form.Item
          name="testStreetAddress"
          label={t('mapTool.addressWhitelistRule.testModal.labelStreetAddress')}
          rules={[{ required: true, message: t('mapTool.addressWhitelistRule.testModal.ruleStreetAddressRequired') as string }]}
        >
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item
          name="testUnit"
          label={t('mapTool.addressWhitelistRule.testModal.labelUnit')}
        >
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item
          name="testCity"
          label={t('mapTool.addressWhitelistRule.testModal.labelCity')}
          rules={[{ required: true, message: t('mapTool.addressWhitelistRule.testModal.ruleCityRequired') as string }]}
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item
          name="testState"
          label={t('mapTool.addressWhitelistRule.testModal.labelState')}
          rules={[{ required: true, message: t('mapTool.addressWhitelistRule.testModal.ruleStateRequired') as string }]}
        >
          <Input maxLength={50} />
        </Form.Item>
        <Form.Item
          name="testZipCode"
          label={t('mapTool.addressWhitelistRule.testModal.labelZipcode')}
          rules={[{ required: true, message: t('mapTool.addressWhitelistRule.testModal.ruleZipcodeRequired') as string }]}
        >
          <Input maxLength={10} />
        </Form.Item>
      </Form>

      {result && (
        <div
          className={`mt-2 px-3 py-2 rounded text-sm border ${
            result.matched
              ? 'bg-green-50 text-green-600 border-green-200'
              : 'bg-red-50 text-red-600 border-red-200'
          }`}
        >
          <div>
            {result.matched
              ? t('mapTool.addressWhitelistRule.testModal.matched')
              : t('mapTool.addressWhitelistRule.testModal.notMatched')}
          </div>
          <div className="mt-1 text-gray-500">
            {`${t('mapTool.addressWhitelistRule.testModal.fullAddress')}: ${result.fullAddress}`}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default TestRuleModal;
